import type {Element, ElementContent, Root} from 'hast'
import {visit} from 'unist-util-visit'

import {isTag} from '../hast-utils'
import {delimiterProperty} from './delimiters'

// Extensions that the editor knows how to render with a native <video> element.
const VIDEO_EXTENSIONS = [
  '.mp4',
  '.m4v',
  '.mov',
  '.webm',
  '.ogv',
  '.ogg',
  '.avi',
  '.mkv',
  '.3gp',
  '.3g2',
  '.3gpp',
  '.mpeg',
  '.mpg',
  '.wmv',
]

const GITHUB_ATTACHMENT_HOSTS = new Set(['github.com', 'www.github.com', 'gist.github.com'])
const GITHUB_ATTACHMENT_PATH_PREFIX = '/user-attachments/assets/'

function tryParseUrl(href: string): URL | undefined {
  try {
    const origin =
      typeof window !== 'undefined' && window.location?.origin ? window.location.origin : 'https://github.com'
    return new URL(href, origin)
  } catch {
    return undefined
  }
}

const hasVideoExtension = (value: string) => {
  const lower = value.toLowerCase()
  return VIDEO_EXTENSIONS.some(extension => lower.endsWith(extension))
}

// Returns true when the provided href points directly to a video asset we can embed.
export function isRenderableVideoUrl(href: unknown): href is string {
  if (typeof href !== 'string') return false
  const trimmed = href.trim()
  if (!trimmed) return false

  const parsed = tryParseUrl(trimmed)
  if (parsed) {
    if (hasVideoExtension(parsed.pathname)) return true

    const downloadName = parsed.searchParams.get('download') ?? parsed.searchParams.get('filename')
    if (downloadName && hasVideoExtension(downloadName)) return true

    if (GITHUB_ATTACHMENT_HOSTS.has(parsed.hostname) && parsed.pathname.startsWith(GITHUB_ATTACHMENT_PATH_PREFIX)) {
      return true
    }
  }

  const withoutHash = trimmed.split('#', 1)[0] ?? trimmed
  const withoutQuery = withoutHash.split('?', 1)[0] ?? withoutHash
  const lowerPath = withoutQuery.toLowerCase()

  return VIDEO_EXTENSIONS.some(extension => lowerPath.endsWith(extension))
}

// Grabs the concatenated text nodes from the element so we can preserve the link label.
function getTextContent(node: Element): string {
  let textContent = ''

  const stack: ElementContent[] = [...(node.children ?? [])]
  while (stack.length > 0) {
    const child = stack.pop()
    if (!child) continue

    if (child.type === 'text') {
      textContent = `${child.value}${textContent}`
    } else if (child.type === 'element') {
      if (child.properties?.[delimiterProperty]) continue
      stack.push(...(child.children ?? []))
    }
  }

  return textContent.trim()
}

// Replaces anchor tags that link to raw videos with <video> nodes that play inline.
export function convertVideoLinks(tree: Root): void {
  visit(tree, 'element', (node, index, parent) => {
    if (!parent || typeof index !== 'number') return
    if (!isTag(node, 'a')) return

    const href = node.properties?.href
    if (!isRenderableVideoUrl(href)) return

    const textContent = getTextContent(node)
    const labelSource = typeof href === 'string' ? href.split('#', 1)[0]?.split('?', 1)[0] ?? href : ''
    const ariaLabel = textContent || labelSource || 'Video player'
    const videoNode: Element = {
      type: 'element',
      tagName: 'video',
      properties: {
        src: href,
        controls: true,
        preload: 'metadata',
        playsInline: true,
        'aria-label': ariaLabel,
        dataOriginalHref: href,
        ...(textContent ? {dataOriginalText: textContent} : {}),
      },
      children: [],
    }

    if (node.position) {
      videoNode.position = {
        start: {...node.position.start},
        end: {...node.position.end},
      }
    }

    parent.children[index] = videoNode
  })
}
